import { reportSGM } from '@jdt-jinke/jdpay_utils'
import { sleep } from '@/hooks/useTools'
import type { ColorConfig, SkinThemeConfig, SkinTheme } from './types'
import { SKIN_COLORS_CONFIG } from './config'
import { pxToRem } from '@/styles/utils'

/** 计算页面需要的色值 */
export function initActivitySkin(color: string): boolean {
  console.log('----------- initActivityColors start ----------', color)
  // 非空校验
  if (!color) {
    reportSGM({
      code: 'initActivitySkin_no_colorValue',
      type: 3
    })
    return false
  }
  // 储值金配色方案转化
  const colorConfig = transformColorConfig(color, SKIN_COLORS_CONFIG)
  // 将配置方案设置成全局变量
  setPageSkinTheme(colorConfig)
  console.log('----------- initActivityColors end ----------', colorConfig)
  return true
}

/** 颜色转化 */
export function transformColorConfig(
  color: string,
  config: Record<string, ColorConfig>
): SkinThemeConfig[] {
  try {
    // 数据格式校验并兼容
    if (typeof config !== 'object') {
      reportSGM({
        code: 'transformColorConfig_no_config',
        type: 1
      })
      return []
    }
    // 取出 colorKey
    const colorConfig = Object.keys(config).map((colorKey) => {
      const { opacity, key, blendColor = '#ffffff' } = config[colorKey] || {}
      const value = blendColors(color, opacity, blendColor)
      return { colorKey, key, value }
    })
    reportSGM({
      code: 'transformColorConfig_colorConfig',
      type: 3,
      res: { colorConfig, color, config }
    })
    return colorConfig
  } catch (error) {
    reportSGM({
      code: 'transformColorConfig_error',
      type: 1,
      err: error,
      res: { color, config }
    })
    return []
  }
}

/**
 * 初始化活动皮肤主题
 * @param {SkinThemeConfig[]} config
 * @param {SkinTheme} skinTheme
 */
export function setPageSkinTheme(
  config: SkinThemeConfig[],
  skinTheme: SkinTheme = {}
): void {
  try {
    // 选择 :root 伪类对应的元素，用于设置全局 CSS 变量
    const root = document?.documentElement
    // 元素校验
    if (!root) {
      reportSGM({
        code: 'setPageSkinTheme_no_root'
      })
      return
    }

    console.log('setPageSkinTheme', config, skinTheme)

    // config非空校验，异常上报
    if (!config?.length) {
      reportSGM({
        code: 'setPageSkinTheme_no_config'
      })
    }

    // 遍历转化器配置
    config?.forEach(
      ({ key, sizeKey, colorKey, bgUrlKey, value, transform } = {}) => {
        // TODO: !value情况目前暂时未使用，后续使用需要再review
        if (!value) {
          value = skinTheme[sizeKey || colorKey || bgUrlKey]
          // 如果是尺寸
          sizeKey && (value = pxToRem(value))
          // 如果是背景图
          bgUrlKey && (value = `url(${value})`)
          // 如果有转化函数
          typeof transform === 'function' && (value = transform(skinTheme))
        }
        // 最后进行赋值
        typeof value === 'string' && root?.style?.setProperty(key, value)
      }
    )
  } catch (error) {
    reportSGM({
      code: 'setPageSkinTheme_error',
      type: 1,
      err: error,
      res: { config, skinTheme }
    })
  }
}

/**
 * 重置页面皮肤主题
 */
export async function resetPageSkinTheme(): Promise<void> {
  await sleep(200)
  console.log('resetPageSkinTheme', document?.documentElement?.style)
  Object.keys(SKIN_COLORS_CONFIG)?.forEach((item) => {
    const key = SKIN_COLORS_CONFIG[item]?.key
    if (key) {
      document?.documentElement?.style?.removeProperty(key)
    }
  })
}

/*** 私有方法 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return { r, g, b }
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

/*** 颜色混合 */
export function blendColors(
  color: string,
  alpha: number,
  background: string = '#ffffff'
): string {
  const colorRgb = hexToRgb(color)
  const backgroundRgb = hexToRgb(background)
  const r = Math.round((1 - alpha) * backgroundRgb.r + alpha * colorRgb.r)
  const g = Math.round((1 - alpha) * backgroundRgb.g + alpha * colorRgb.g)
  const b = Math.round((1 - alpha) * backgroundRgb.b + alpha * colorRgb.b)

  return rgbToHex(r, g, b)
}
